/**
 * Типы домена. Структурные (а не привязанные к Drizzle) — этими же функциями пользуется
 * клиентский стор, у которого строк БД нет.
 */

/**
 * `focus` — заход в фокус: нода, которую заводят ПОД период работы («Хрупкие люди —
 * 2026-08-31 — 2026-09-06») и закрывают галочкой, когда период отработан. Это тип
 * ноды, а не пометка на чужой: системного контейнера у фокуса нет — панель и `/focus`
 * собираются фильтром по роли, как `/projects` (impl-log).
 */
export const USER_ROLES = ["plain", "task", "project", "recurring", "focus"] as const;

/**
 * Полный список системных ролей — ОДИН на всю систему.
 * Перечислять их по месту запрещено: именно так `pinned` и оказался вне защиты,
 * будучи добавленным в модель (service-layer §5.0).
 */
export const SYSTEM_ROLES = ["root", "day", "calendar", "inbox", "pinned"] as const;

export const NODE_ROLES = [...USER_ROLES, ...SYSTEM_ROLES] as const;

export const NODE_STATUSES = ["open", "paused", "someday", "done", "cancelled"] as const;

export const NODE_DIFFICULTIES = ["tiny", "normal", "hard"] as const;

export const NODE_RELATIONS = ["mention"] as const;

/**
 * Палитра тегов — КЛЮЧИ, а не hex. Сам оттенок задаёт тема (`styles.css`, `--tag-*`):
 * один и тот же «синий» в светлой и тёмной теме обязан быть разным, иначе в одной из
 * них плашку не прочесть. Ключи хранятся в базе — переименовывать их нельзя, только добавлять.
 */
export const TAG_COLORS = ["red", "orange", "amber", "green", "teal", "blue", "violet", "pink", "gray"] as const;
export type TagColor = (typeof TAG_COLORS)[number];

export type UserRole = (typeof USER_ROLES)[number];
export type SystemRole = (typeof SYSTEM_ROLES)[number];
export type NodeRole = (typeof NODE_ROLES)[number];
export type NodeStatus = (typeof NODE_STATUSES)[number];
export type NodeDifficulty = (typeof NODE_DIFFICULTIES)[number];
export type NodeRelation = (typeof NODE_RELATIONS)[number];

/** Канал операции. Backend выводит его из способа аутентификации (engineering §6.1). */
export type OpSource = "web" | "rest" | "mcp";

/** Дата без времени, 'YYYY-MM-DD'. Ни `scheduled_date`, ни `day_date` не имеют таймзоны. */
export type PlainDate = string;

export type Recurrence = {
	/** Единица, за которую считается цель. */
	period: "day" | "week" | "month";
	/** Цель за период; по умолчанию 1. */
	count?: number;
	/** Только при period='day': активна лишь в эти дни недели (0 = воскресенье). */
	daysOfWeek?: number[];
	/** Только при period='month': конкретное число. «31-е» клампится к последнему дню месяца. */
	dayOfMonth?: number;
	/** Слоты внутри дня, метаданные — модель выполнения не меняют. */
	times?: string[];
};

/**
 * Минимальная форма ноды, которой достаточно чистым правилам. Строка БД ей соответствует
 * структурно; клиентский `NodeView` — тоже.
 */
export type NodeLike = {
	id: string;
	parentId: string | null;
	position: string;
	role: NodeRole;
	title: string;
	body: string | null;
	status: NodeStatus | null;
	scheduledDate: PlainDate | null;
	dayDate: PlainDate | null;
	completedAt: Date | null;
	archivedAt: Date | null;
	archivedOp: string | null;
	recurrence: Recurrence | null;
	tags: string[];
	contentVersion: number;
};

export type PlacementLike = {
	id: string;
	nodeId: string;
	containerId: string;
	position: string;
};

export type LinkLike = {
	id: string;
	fromNodeId: string;
	toNodeId: string;
	relation: NodeRelation;
};

/**
 * Оформление тега — отдельно от нод: тег это строка в `nodes.tags`, а цвет — свойство
 * самого тега, общее для всех нод. Строки нет — цвета нет, тег рисуется нейтрально.
 */
export type TagLike = {
	id: string;
	name: string;
	color: TagColor;
};

export type CompletionLike = {
	id: string;
	nodeId: string;
	completedOn: PlainDate;
	clientEventId: string | null;
};
